import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';

import type { WalletPaymentAccountLinkedItemType } from '@pages/organizer/Finance/components/tabs/Wallet/store/wallet-types';
import type { WalletPaymentAccountType } from '@pages/organizer/Finance/components/tabs/Wallet/store/wallet-types';
import type { ErrorType } from '@shared/interfaces/error.interfaces';
import type { HubLinkingModel } from '@shared/models/hubLinking.model';
import type { AppDispatch } from '@store/store';

export const getWalletLinkBeginToken = createAsyncThunk<
  { token: string; item_id: number },
  undefined,
  { rejectValue: ErrorType }
>('getWalletLinkBeginToken', async (_, { rejectWithValue }) => {
  try {
    return await api.finance.getWalletLinkBeginToken();
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getWalletLinkReAuthToken = createAsyncThunk<
  { token: string; item_id: number },
  { item_id: number },
  { rejectValue: ErrorType }
>('getWalletLinkReAuthToken', async (params, { rejectWithValue }) => {
  try {
    const response = await api.finance.getWalletLinkReAuthToken(params);
    return { token: response.token, item_id: params.item_id };
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getWalletAccounts = createAsyncThunk<
  { data: WalletPaymentAccountType[]; meta: { net_balance: number } },
  { types?: string[] } | undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getWalletAccounts', async (props, { rejectWithValue }) => {
  try {
    return await api.finance.getWalletAccounts(props);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getWalletLinkedItems = createAsyncThunk<
  WalletPaymentAccountLinkedItemType[],
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getWalletLinkedItems', async (_, { rejectWithValue }) => {
  try {
    return await api.finance.getWalletLinkedItems();
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getWalletUnsynchronizedAccounts = createAsyncThunk<
  { data: WalletPaymentAccountType[]; meta: { net_balance: number } },
  { types?: string[] } | undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getWalletUnsynchronizedAccounts', async (props, { rejectWithValue }) => {
  try {
    return await api.finance.getWalletUnsynchronizedAccounts(props);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getWalletAccount = createAsyncThunk<
  WalletPaymentAccountType,
  number,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getWalletAccount', async (props, { rejectWithValue }) => {
  try {
    return await api.finance.getWalletAccount(props);
  } catch (e: any) {
    if (e.status === 403) {
      NotificationService.info(i18next.t('general.notifications.notAssignedToWalletAccount'));
    } else {
      NotificationService.info(i18next.t('general.notifications.accountNotFound'));
    }

    return rejectWithValue(e.data as ErrorType);
  }
});

export const completeWalletLinkAccount = createAsyncThunk<
  undefined,
  { token: string; item_id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getWalletAccounts', async (params, { rejectWithValue }) => {
  try {
    return await api.finance.completeWalletLinkAccount(params);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});
export const updateWalletAccount = createAsyncThunk<
  WalletPaymentAccountType,
  { custom_name?: string; linking?: HubLinkingModel | null; item_id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateWalletAccount', async (params, { rejectWithValue }) => {
  try {
    return await api.finance.updateWalletAccount(params);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const unlinkWalletAccount = createAsyncThunk<
  number,
  { item_id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('unlinkWalletAccount', async (params, { rejectWithValue }) => {
  try {
    return await api.finance.unlinkWalletAccount(params).then(() => params.item_id);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const activateWalletAccount = createAsyncThunk<
  { activatedItem: WalletPaymentAccountType; previousActivatedItemId: number },
  { item_id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('activateWalletAccount', async (params, { rejectWithValue }) => {
  try {
    return await api.finance.activateWalletAccount(params).then((result) => {
      return { activatedItem: result, previousActivatedItemId: params.item_id };
    });
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});
